import { IAccountsPayable, IAccountsPayableDetail } from "@nkr/models/reports";
import { Button, Col, FormControl, Modal, Row } from "react-bootstrap";
import CcbPayNowForm from "../accounts-payable/pay-now";
import { AgGridReact } from "ag-grid-react";
import { DetailReportWrapper } from "../accounts-payable/styles";
import { useState } from "react";
import { ColDef, ColumnApi, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, ValueGetterParams } from "ag-grid-community";
import SortableHeaderComponent from "@nkr/views/admin/center/list/common/sortable-header";
import useAPDetails from "@nkr/store/reports/hooks/use-ap-details";
import RankRenderer from "@nkr/views/admin/center/list/common/rank-renderer";
import * as moment from 'moment';
import * as _ from 'lodash';

type TProps = {
    ap: IAccountsPayable,
    onMarkPaid: (achConfirmation: string, amountPaid: number, lineItems: IAccountsPayableDetail[]) => void
};

export default function DetailsModal({ ap, onMarkPaid }: TProps) {
    const [show, setShow] = useState<boolean>(false);
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null);
    const [billingType, setBillingType] = useState<string>('');

    const { list, setChecked, checkPayNow, isPayable } = useAPDetails(ap, show);

    const columns: ColDef[] = [
        {
            colId: 'isChecked',
            headerName: 'C',
            width: 50,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>C</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '') {
                    return (
                        <input type="checkbox" checked={params.data.isChecked} onClick={() => { setChecked(params.data) }} />
                    );
                }

                return null;
            },
        },

        {
            colId: 'rank',
            headerName: 'Rank',
            width: 50,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R</SortableHeaderComponent>,
            pinnedRowCellRendererFramework: (params: ICellRendererParams) => 'Totals:',
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '')
                    return (
                        <RankRenderer {...params} />
                    );

                return null;
            },
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },

        {
            colId: 'billingType',
            field: 'billingType',
            headerName: 'billingType',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '') {
                    return <>{params.data.billingType}</>
                } else {
                    return 'Totals'
                }
            },
        },

        {
            colId: 'swapNumber',
            field: 'swapNumber',
            headerName: 'Swap #',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
        },

        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor</SortableHeaderComponent>,
        },

        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient</SortableHeaderComponent>,
        },
        {
            colId: 'daysOutstanding',
            field: 'daysOutstanding',
            headerName: 'Days O/S',
            width: 70,
            suppressSizeToFit: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Days O/S</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '') {
                    return params.value;
                }

                return '~' + params.value;
            },
        },

        {
            colId: 'amount',
            field: 'amount',
            headerName: 'Amount',
            width: 100,
            sortingOrder: ['desc', 'asc'],
            suppressSizeToFit: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Amount</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.data.amount.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        {
            colId: 'billableCenterAlias',
            field: 'billableCenterAlias',
            headerName: 'Billable Center',
            width: 130,
            sortingOrder: ['desc', 'asc'],
            suppressSizeToFit: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Billable Center</SortableHeaderComponent>,
        },

        {
            colId: 'isPaid',
            field: 'isPaid',
            headerName: 'Paid',
            width: 100,
            sortingOrder: ['desc', 'asc'],
            suppressSizeToFit: false,
            pinnedRowCellRendererFramework: (params: ICellRendererParams) => '',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Paid</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.data.isPaid ? <span className='label label-success'>Y</span> : '-',
        },

        {
            colId: 'invoiceDate',
            field: 'invoiceDate',
            headerName: 'Invoice Date',
            width: 110,
            sortingOrder: ['desc', 'asc'],
            suppressSizeToFit: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoice Date</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => moment(params.data.invoiceDate).isValid() ? moment(params.data.invoiceDate).format('MM/DD/YYYY') : '--',
        },
    ];

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
        setColumnApi(e.columnApi);

        e.columnApi.applyColumnState({
            state: [
                { colId: 'centerHasLatePaymentsInLastYear', sort: 'asc' }
            ]
        });
    };

    const handleMarkPaid = (achConfirmation: string, amountPaid: number, lineItems: IAccountsPayableDetail[]) => {
        onMarkPaid(achConfirmation, amountPaid, lineItems);
        setShow(false);
    }

    const handleClose = () => {
        setShow(false);
    }

    const typeList = _.uniq(list.map(x => x.billingType));

    const filtered = list.filter(li => billingType == '' || li.billingType == billingType)

    return (<>
        <Button bsSize="xs" bsStyle={isPayable ? 'success' : 'primary'} onClick={() => setShow(true)}>{isPayable ? 'Pay' : 'Details'}</Button>

        <Modal backdrop='static' className='modal-xxl' bsSize='lg' show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Payable Center: {ap.centerAlias}</Modal.Title>
            </Modal.Header>

            <Modal.Body >
                {!isPayable &&
                    <Row className="row-margined">
                        <Col xs={6} sm={2}>
                            <FormControl
                                bsSize='sm'
                                componentClass='select'
                                value={billingType}
                                onChange={(e: React.FormEvent<FormControl>) => setBillingType((e.target as HTMLSelectElement).value)}
                            >
                                <option value=''>- All Types -</option>
                                {typeList.map((t, i) =>
                                    <option key={'type-' + i} value={t}>{t}</option>
                                )}
                            </FormControl>
                        </Col>
                        <Col xs={6} sm={10}>
                            <button className="btn btn-primary pull-right" type="button" onClick={checkPayNow}>Check Pay Now</button>
                        </Col>
                    </Row>
                }

                <DetailReportWrapper style={{ height: 500 }}>
                    <AgGridReact
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true,
                            suppressSizeToFit: true,
                        }}
                        columnDefs={columns}
                        rowData={filtered}
                        suppressRowClickSelection={false}
                        suppressCellSelection={true}
                        suppressContextMenu={true}
                        pinnedTopRowData={[{
                            invoiceDate: '',
                            swapNumber: '',
                            amount: list.reduce((a, v) => a = a + v.amount, 0),
                            daysOutstanding: Math.round(_.sumBy(list, x => x.daysOutstanding) / list.length)
                        }]}
                        noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                        onGridReady={onGridReady}
                        onFirstDataRendered={() => gridApi.sizeColumnsToFit()}
                        onSortChanged={() => gridApi.refreshCells()}
                        getRowStyle={(props: any) => {
                            if (props.node.rowPinned) {
                                return { backgroundColor: 'lightblue', fontWeight: 700 } as any;
                            }
                        }}
                    />
                </DetailReportWrapper>

                <div>{filtered.length} of {list.length} items...</div>

                <CcbPayNowForm
                    data={list}
                    onMarkPaid={handleMarkPaid}
                    confirmationNumber={ap.confirmationNumber} />

            </Modal.Body>
            <Modal.Footer>
                <Button onClick={handleClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    </>);
}